import { useEffect, useState } from 'react';

import type { HubItemDetail, HubItemKind } from '../api/types';

import { fetchHubItem } from '../api/infoHubApi';
import { extractRenderableHtml } from '../utils/contentHtml';

export type HubItemState = 'loading' | 'ready' | 'notfound' | 'error';

export interface UseHubItemResult {
  item: HubItemDetail | null;
  /** Render-ready HTML, wrappers stripped. Empty when the item has no body. */
  html: string;
  state: HubItemState;
}

/**
 * Loads a single article or news item by slug (or GUID) and prepares its HTML for rendering.
 *
 * `notfound` and `error` stay distinct, as everywhere else in the hub: a missing or inactive
 * item is an expected outcome the reader should see a friendly message for, not a failure.
 */
export const useHubItem = (kind: HubItemKind, idOrSlug: string): UseHubItemResult => {
  const [item, setItem] = useState<HubItemDetail | null>(null);
  const [html, setHtml] = useState('');
  const [state, setState] = useState<HubItemState>('loading');

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setItem(null);
    setHtml('');

    const key = (idOrSlug || '').trim();
    if (!key) {
      setState('notfound');
      return;
    }

    const load = async (): Promise<void> => {
      try {
        const result = await fetchHubItem(kind, key);
        if (cancelled) {
          return;
        }

        if (!result) {
          setState('notfound');
          return;
        }

        setItem(result);
        setHtml(extractRenderableHtml(result.contentHtml));
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading hub item:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [kind, idOrSlug]);

  return { item, html, state };
};
